import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { ImageMap } from '../../../ImageMap/ImageMap';

const getAreaSizesByCoords = (coords) => {
  const xArray = coords.map((coord) => coord.x);
  const yArray = coords.map((coord) => coord.y);
  const minX = Math.min(...xArray);
  const minY = Math.min(...yArray);
  const maxX = Math.max(...xArray);
  const maxY = Math.max(...yArray);
  return {
    top: minY,
    left: minX,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const getMapDataFromDeckPlan = (
  deckPlan,
  name,
  availableCabins,
  selectedStateroomCabin,
  otherStateroomCabins,
  filteredCabins,
) => {
  if (!deckPlan || !deckPlan.mapData) return { areas: [] };
  const md = JSON.parse(deckPlan.mapData);
  return {
    areas: md.map.areas.map((a) => {
      const foundCabin = filteredCabins
        ? filteredCabins.find((c) => c.cabinNumber === a.title)
        : availableCabins.find((c) => c.cabinNumber === a.title);
      let isSelectedCabin = a.title === selectedStateroomCabin;
      let selectedCabinStateroom = -1;
      if (!isSelectedCabin && otherStateroomCabins && otherStateroomCabins[a.title] > -1) {
        selectedCabinStateroom = otherStateroomCabins[a.title];
        if (selectedCabinStateroom > -1) {
          isSelectedCabin = true;
        }
      }
      const availableCabinColor = 'rgba(0, 255, 0, 0.3)';
      const unavailableCabinColor = 'rgba(0, 0, 0, 0)';
      const preFillColor = foundCabin ? availableCabinColor : unavailableCabinColor; // colors based on availability
      return {
        shape: a.shape,
        coords: a.coords,
        sizes: getAreaSizesByCoords(a.coords),
        title: a.title,
        isSelectedCabin,
        selectedCabinStateroom,
        preFillColor,
        isAvailable: !!foundCabin,
      };
    }),
    name: name || md.map.name,
  };
};
const DeckPlan = ({
  deckPlan,
  onClose,
  deckPlans,
  onSelect,
  key,
  selectedStateroomIndex,
  filteredCabins,
}) => {
  const [selectedDeckPlan, setSelectedDeckPlan] = useState(deckPlan);
  const [wrapperWidth, setWrapperWidth] = useState(null);
  const { availableCabins, currentTripId, deckLegends } = useSelector((state) => state.cruise);
  const selectedTrip = useSelector((state) => state.trip.selectedTrips[currentTripId]);
  const { track } = useAnalytics();
  const ref = useRef(null);
  const ruler = useRef(null);
  const [selectedStateroomCabin, setSelectedStateroomCabin] = useState(undefined);
  const [otherStateroomCabins, setOtherStateroomCabins] = useState(undefined);

  const handleChange = (e) => {
    const deck = deckPlans.find((d) => d.shipDeckName === e.target.value);
    setSelectedDeckPlan(deck);
    track('deckPlan.handleChange', {
      name: e.target.value,
    });
  };

  const handleMapClick = (area) => {
    if (onSelect && area && area.title) onSelect(area.title);
  };

  const handleMouseEnterArea = (area, areaCenter) => {
    const { current } = ref;
    const tooltip = document.createElement('div');
    tooltip.style.cssText = `
      left: calc(${areaCenter.x}% - 100px);
      top: calc(${areaCenter.y}% - 60px);
      z-index: 2;
      width: 200px;
      position: absolute;
      background: rgba(255, 255, 255, 0.8);
      border: 1px solid black;
      border-radius: 0.25rem;
      padding: 5px;
      box-shadow: rgba(0, 0, 0, 0.3) 0 2px 10px;`;

    let label = area.isAvailable ? 'Available' : 'Unavailable';
    if (area.isSelectedCabin) {
      label = 'Selected';
      if (area.selectedCabinStateroom > -1) {
        label += ` as Stateroom ${area.selectedCabinStateroom + 1}`;
      }
    }
    tooltip.innerHTML = `${area.title} (${label})`;
    tooltip.classList.add('deck-plan-tooltip');
    current.appendChild(tooltip);
  };

  const handleMouseLeaveArea = () => {
    const { current } = ref;
    const tooltip = current.getElementsByClassName('deck-plan-tooltip')[0];
    if (tooltip) tooltip.remove();
  };

  useEffect(() => {
    setSelectedDeckPlan(deckPlan);
  }, [deckPlan]);

  useEffect(() => {
    if (ruler) {
      setWrapperWidth(ruler.current.clientWidth);
    }
  }, [ruler]);

  useEffect(() => {
    if (
      selectedTrip &&
      selectedTrip.cruiseItinerary &&
      selectedTrip.cruiseItinerary.cabins &&
      selectedTrip.cruiseItinerary.cabins.length
    ) {
      if (selectedTrip.cruiseItinerary.cabins[selectedStateroomIndex]) {
        setSelectedStateroomCabin(
          selectedTrip.cruiseItinerary.cabins[selectedStateroomIndex].selectedShipCabin.cabinNumber,
        );
      }

      const dict = {};
      _.filter(
        selectedTrip.cruiseItinerary.cabins,
        (c) => c && c.selectedShipCabin && c.roomIndex !== selectedStateroomIndex,
      ).forEach((c) => {
        dict[c.selectedShipCabin.cabinNumber] = c.roomIndex;
      });
      setOtherStateroomCabins(dict);
    }
  }, [selectedStateroomIndex, selectedTrip]);

  return (
    <>
      {onClose && <FaTimes className="float-right m-2 cursor-pointer" onClick={onClose} />}
      {deckLegends && deckLegends.length > 0 && (
        <div className="py-2 pb-4 space-y-4">
          <div className="text-lg font-bold mt-2">Deck Plan Legend (Key to Symbols)</div>
          <div className="grid grid-cols-2 text-left px-4 ml-4">
            {deckLegends.map((deckLegend, index) => (
              <div key={`${index.toString()}`} className="flex items-center">
                <img
                  className="w-6 h-6 object-contain mr-3"
                  alt={deckLegend.attribute}
                  src={deckLegend.logo}
                />
                <span className="text-sm font-medium text-gray-800">{deckLegend.attribute}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <h1 ref={ruler} className="text-lg font-bold mt-2">
        {!deckPlans && selectedDeckPlan && selectedDeckPlan.shipDeckName}
        {deckPlans && (
          <select
            onChange={handleChange}
            value={selectedDeckPlan && selectedDeckPlan.shipDeckName}
            className="font-bold"
          >
            {!selectedDeckPlan && <option>Select deck to view</option>}
            {deckPlans.map((d) => (
              <option key={d.shipDeckName} value={d.shipDeckName}>
                {d.shipDeckName}
              </option>
            ))}
          </select>
        )}
      </h1>
      {selectedDeckPlan && (
        <div className="text-center full-width">
          {!onSelect && (
            <img
              className="mx-auto my-10 mt-4"
              src={selectedDeckPlan.shipDeckImage}
              alt={selectedDeckPlan.shipDeckName}
              title={selectedDeckPlan.shipDeckName}
            />
          )}
          {onSelect && wrapperWidth !== null && document && (
            <ImageMap
              ref={ref}
              src={selectedDeckPlan.shipDeckImage}
              map={getMapDataFromDeckPlan(
                selectedDeckPlan,
                key,
                availableCabins,
                selectedStateroomCabin,
                otherStateroomCabins,
                filteredCabins,
              )}
              onClick={handleMapClick}
              onMouseEnter={handleMouseEnterArea}
              onMouseLeave={handleMouseLeaveArea}
            />
          )}
        </div>
      )}
    </>
  );
};

DeckPlan.propTypes = {
  deckPlan: PropTypes.shape({
    shipDeckImage: PropTypes.string,
    shipDeckName: PropTypes.string,
    shipDeckOrder: PropTypes.number,
    mapData: PropTypes.string,
  }),
  onClose: PropTypes.func,
  deckPlans: PropTypes.arrayOf(
    PropTypes.shape({
      shipDeckImage: PropTypes.string,
      shipDeckName: PropTypes.string,
      shipDeckOrder: PropTypes.number,
    }),
  ),
  onSelect: PropTypes.func,
  key: PropTypes.string,
  selectedStateroomIndex: PropTypes.number,
  filteredCabins: PropTypes.PropTypes.arrayOf(
    PropTypes.shape({
      cabinBedTypeOptions: PropTypes.arrayOf(PropTypes.string),
      cabinBedTypes: PropTypes.arrayOf(PropTypes.objectOf),
      cabinDeckName: PropTypes.string,
      cabinLocations: PropTypes.arrayOf(PropTypes.string),
      cabinMaxPassengers: PropTypes.string,
      cabinMinPassengers: PropTypes.string,
      cabinNumber: PropTypes.string,
      connectingStateroom: PropTypes.string,
      isHandicapAccessible: PropTypes.bool,
    }),
  ),
};

DeckPlan.defaultProps = {
  deckPlan: undefined,
  onClose: undefined,
  deckPlans: undefined,
  onSelect: undefined,
  key: undefined,
  selectedStateroomIndex: 0,
  filteredCabins: undefined,
};

export default DeckPlan;
